import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useSelector } from "react-redux";
import {
  resendVerificationEmail,
  selectEmailVerificationResponse,
  selectVerifyingEmail,
  verifyEmail,
} from "./ValidateEmailSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { toast } from "react-toastify";

type Props = {
  userID: string;
};

const validateEmailSchema = Yup.object().shape({
  verificationCode: Yup.string()
    .required("Please enter your verification code")
    .matches(/^.*(?=.{5,})(?=.*\d).*$/, "Verification code must be a 5 digit number"),
});

const ValidateEmail: React.FC<Props> = ({ userID }) => {
  const thunkDispatch = useThunkDispatch();
  const verifyingEmail = useSelector(selectVerifyingEmail);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const response = useSelector(selectEmailVerificationResponse);

  useEffect(() => {
    if (response) {
      window.location.href = "/dashboard";
    }
  }, [response]);

  const initialValues = {
    verificationCode: "",
  };

  function resendCode() {
    setButtonDisabled(true);
    thunkDispatch(resendVerificationEmail({ userID }));
    setTimeout(() => {
      setButtonDisabled(false);
    }, 5000);
    toast.success("Email sent!");
  }

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header">
        <div className="card-title m-0">
          <h3 className="fw-bolder m-0">Verify your email</h3>
        </div>
      </div>
      <div className="card-body border-top p-9">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={async (values) => {
            thunkDispatch(verifyEmail({ userID: userID, verificationCode: values.verificationCode }));
          }}
          validationSchema={validateEmailSchema}
        >
          {() => (
            <Form>
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Verification Code</label>
                <div className="col-lg-8">
                  <div className="row">
                    <div className="col-lg-12 fv-row">
                      <Field
                        data-test-id="verify-email-form__verification-code-input"
                        name={"verificationCode"}
                        id="verificationCode"
                        className="form-control form-control-lg form-control-solid mb-3 mb-lg-0"
                        type="text"
                      />
                      <div className="fv-plugins-message-container">
                        <div className="fv-help-block">
                          <ErrorMessage name="verificationCode" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="btn-group btn-group-sm" role="group">
                <button
                  data-test-id="verify-email-form__resend-verification-email-button"
                  onClick={resendCode}
                  className="btn btn-small btn-light"
                  disabled={buttonDisabled}
                >
                  Resend Verification Email
                </button>
                <button
                  data-test-id="verify-email-form__submit-button"
                  type="submit"
                  className="btn btn-small btn-primary"
                  disabled={verifyingEmail}
                >
                  {!verifyingEmail && "Submit Verification"}
                  {verifyingEmail && (
                    <span className="indicator-progress" style={{ display: "block" }}>
                      Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ValidateEmail;
